import { VdTracking } from '@vend/vend.js'
import { ModalFactory } from '@vend/vend.ui'
import angular from 'angular'

import { SaleStatus } from '../models/sale.constants'
import parkSaleTemplate from '../../templates/current-sale/action-add-note-park-sale.html'
import outletsDifferTemplate from '../components/sale/outlets-differ-modal/outlets-differ-modal.html'
import erroredSalesTemplate from '../components/sale/errored-sales-modal/errored-sales-modal.html'
import { STATES } from '../../../lib/routes'
import { $q, $state } from '../ng-compat/imports'
import { Sale } from '../models/sale'
import { UserManager } from '../managers/user-manager'
import { WEB_REGISTER } from '../components/web-register.constant'
import { SaleSyncManager } from '../managers/sale-sync-manager'
import { RetailerSettingsManager } from '../managers/retailer-settings-manager'

const SALE_MODALS_CONFIG = {
  CONTEXTS: {
    DEFAULT: 'DEFAULT',
    SWITCH_REGISTERS: 'SWITCH_REGISTERS',
    TRAINING_MODE: 'TRAINING_MODE',
    CLOSE_REGISTER: 'CLOSE_REGISTER',
  },

  ACTION_CONFIRM_SALE: {
    CONTENT: {
      DEFAULT: `You have a sale on the Sell screen that hasn’t been completed. You can choose to return to that sale
        to complete it, or save that sale and continue with this one.`,
      SWITCH_REGISTERS: `Before you can switch registers, you have to complete the sale on the Sell screen. You can
        choose to save that sale and continue on to switch registers, or return to that sale to complete it.`,
      TRAINING_MODE: `Before you can switch to Training Mode, you have to complete the sale on the Sell screen.
        To keep the changes you’ve made to it, return to that sale to complete it. Or you can choose to discard changes
        made to that sale and switch to Training Mode.`,
      CLOSE_REGISTER: `Before you can close your register, you have to complete the sale on the Sell screen.
        You can choose to save that sale and continue to close your register, or return to that sale to complete it.`,
    },
  },
}

const ACTIVE_SALE_EVENT = 'Active Sale Confirmation'

function SaleModalsFactory(
  Modal: ModalFactory,
  Confirm: $TSFixMe,
  wrSaleStatusFilter: $TSFixMe,
  saleSyncManager: SaleSyncManager,
  retailerSettingsManager: RetailerSettingsManager,
  userManager: UserManager,
  vdTracking: VdTracking
) {
  /**
   * Provides sale related modals e.g. to action an existing sale while loading another.
   *
   * @class SaleModals
   * @static
   */
  return {
    /**
     * The available contexts for sale modals.
     *
     * @readOnly
     * @property CONTEXTS
     * @type Object
     */
    get CONTEXTS() {
      return SALE_MODALS_CONFIG.CONTEXTS
    },

    /**
     * Prompts the user to confirm an action for the existing sale (which does not have payments). Parks non "confirmed"
     * sales, updates and finalises Layby or On Account sales.
     *
     * @method actionConfirmSale
     *
     * @param options {Object}
     *
     *        @param options.saveSaleResolver {Function}
     *               Confirmation resolver function, invoked when user selects save and continue option.
     *
     *        @param [options.context] {String}
     *               The context in which the modal is being shown.
     *
     *        @param [options.isAccountBalancePaymentAdded] {boolean}
     *               Flag to indicate if Account Balance Payment is added to sale.
     *
     * @return {Promise} Resolved once the user has chosen a course of action for the current "confirmed" sale.
     */
    actionConfirmSale({
      saveSaleResolver,
      context,
      isAccountBalancePaymentAdded,
    }: {
      saveSaleResolver: $TSFixMeFunction
      context?: string
      isAccountBalancePaymentAdded?: boolean
    }) {
      const isOnCurrentSaleState = STATES.SALE === $state.current.name
      const actionConfirmSaleContent: Record<string, string> =
        SALE_MODALS_CONFIG.ACTION_CONFIRM_SALE.CONTENT

      const content = `<p class="vd-p">${
        actionConfirmSaleContent[context!] || actionConfirmSaleContent.DEFAULT
      }</p>
        ${
          isAccountBalancePaymentAdded
            ? `<p class="vd-p">If you save that sale, the Account Balance Payment you’ve added in the
                Pay screen won’t be remembered when you come back to&nbsp;it.</p>`
            : ''
        }`

      vdTracking.trackUserEvent({
        eventName: ACTIVE_SALE_EVENT,
        metadata: {
          action: 'actionConfirmSale',
          context,
        },
      })

      return Confirm({
        header: 'Hold up! You currently have a sale in progress',
        content,
        cancel: {
          isVisible: false,
        },
        confirm: [
          {
            label: 'Return to Sale in Progress',
            category: 'supplementary',
            resolve: isOnCurrentSaleState ? $q.resolve : this._navSellScreen,
          },
          {
            label: 'Save and Continue',
            resolve: saveSaleResolver,
          },
        ],
      })
    },

    /**
     * Displays a modal highlighting that the current sale contains a gift card and must be completed.
     *
     * @method actionConfirmSaleWithGiftCard
     *
     * @return {Promise} Resolved once the user has confirmed the modal action.
     */
    actionConfirmSaleWithGiftCard() {
      const isOnCurrentSaleState = STATES.SALE === $state.current.name

      vdTracking.trackUserEvent({
        eventName: ACTIVE_SALE_EVENT,
        metadata: {
          action: 'actionConfirmSaleWithGiftCard',
        },
      })

      return Confirm({
        header:
          'Hold up! You currently have a sale in progress, and it contains an unsold Gift Card.',
        content: `<p class="vd-p">You have a sale on the Sell screen that hasn’t been completed. As a precaution,
      unsold Gift Cards cannot be parked with a sale. Please return to the Sell screen to either complete that
      sale or remove the Gift Card to park that sale.</p>`,
        cancel: {
          isVisible: false,
        },
        confirm: [
          {
            label: 'Return to Sale in Progress',
            resolve: isOnCurrentSaleState ? $q.resolve : this._navSellScreen,
          },
        ],
      })
    },

    /**
     * Displays a modal highlighting that the current sale has partial payments and must be completed.
     *
     * @method actionConfirmSaleWithPayments
     *
     * @return {Promise} Resolved once the user has confirmed the modal action.
     */
    actionConfirmSaleWithPayments() {
      const isOnCurrentSaleState = STATES.SALE === $state.current.name

      vdTracking.trackUserEvent({
        eventName: ACTIVE_SALE_EVENT,
        metadata: {
          action: 'actionConfirmSaleWithPayments',
        },
      })

      return Confirm({
        header: 'Please complete your previous sale',
        content: `<p class="vd-p">You’ve taken payments on your previous sale, so that sale must be completed, saved or put on
      account or Layby.</p>`,
        cancel: {
          isVisible: false,
        },
        confirm: [
          {
            label: 'Return to Sale in Progress',
            resolve: isOnCurrentSaleState ? $q.resolve : this._navSellScreen,
          },
        ],
      })
    },

    /**
     * Prompts the user for a note. If a note has not been passed when the modal is closed, we reject the promise. This
     * allows the retailer to dismiss the modal (by pressing escape etc), while keeping their sale open.
     *
     * @method actionAddNoteParkSale
     *
     * @return {Promise<String>} Resolves once the user has entered a note.
     */
    actionAddNoteParkSale(
      currentNote: string,
      isParkSale: boolean,
      isReturn: boolean,
      isAccountBalancePaymentAdded: boolean
    ) {
      return $q((resolve, reject) => {
        return Modal({
          template: parkSaleTemplate,
          controller: 'ActionAddNoteParkSaleController',
          controllerAs: '$ctrl',
          currentNote,
          isParkSale,
          isReturn,
          isAccountBalancePaymentAdded,
        })
          .then((modal: $TSFixMe) => modal.close)
          .then((result: $TSFixMe) => {
            if (result) {
              resolve(result)
            } else {
              reject()
            }
          })
      })
    },

    /**
     * Open the "confirm continue partial paid sale" lightbox that warns the user that the parked sale they are
     * about to retrieve has partial payments on it and will not be able to be re-parked.
     *
     * @method confirmContinuePartialPaidSale
     *
     * @return {Promise} Resolved once the user has confirmed the modal action.
     */
    confirmContinuePartialPaidSale() {
      vdTracking.trackUserEvent({
        eventName: ACTIVE_SALE_EVENT,
        metadata: {
          action: 'confirmContinuePartialPaidSale',
        },
      })

      return Confirm({
        header: 'Continue sale with existing payments?',
        content: `<p class="vd-p">You’ve already taken some payments for this sale. If you continue the sale, you
      will not be able to park the sale again.</p>
      <p class="vd-p">You must:</p>
      <ul class="wr-bulleted-list">
        <li>Complete the sale by taking full payment.</li>
        <li>Change the sale to an ${wrSaleStatusFilter(
          SaleStatus.ACCOUNT_SALE
        )} or
          ${wrSaleStatusFilter(SaleStatus.LAYBY_SALE)} sale.
        </li>
      </ul>`,
        confirm: {
          label: 'Continue sale',
          category: 'no',
        },
      })
    },

    /**
     * Shown when attempting to load an `on Account` or `Layby` sale initiated in a different outlet to the current
     * active outlet.
     *
     * @method warnThatOutletsDiffer
     *
     * @return {Promise}
     */
    warnThatOutletsDiffer({ saleStatus, saleOutletName }: $TSFixMe) {
      return Modal({
        template: outletsDifferTemplate,
        controller: 'OutletsDifferModalController',
        controllerAs: '$ctrl',
        saleOutletName,
        saleStatus,
      })
        .then((modal: $TSFixMe) => modal.close)
        .then(() => $q.reject())
    },

    /**
     * Modal shown when dismissing a confirmed (Layby or On Account) or a not fully paid unfulfilled
     * sale that has not been completed yet.
     *
     * @method dismissSale
     *
     * @param {String} saleStatus
     *        The status of the sale.
     *
     * @return {Promise} Resolved once the user has chosen a course of action.
     */
    dismissSale(saleStatus: SaleStatus.LAYBY_SALE | SaleStatus.ACCOUNT_SALE) {
      const statusMessage = {
        [SaleStatus.LAYBY_SALE]: 'Layby',
        [SaleStatus.ACCOUNT_SALE]: 'Account',
        [SaleStatus.AWAITING_DISPATCH]: 'Delivery, unfulfilled',
        [SaleStatus.AWAITING_PICKUP]: 'Pickup, unfulfilled',
        [SaleStatus.SERVICE]: 'Service',
      }

      return Confirm({
        header: 'What would you like to do with this sale?',
        content: `<p class="vd-p" vd-no-widow>You haven't completed this sale and are about to lose the changes you've
      made. To keep those changes, continue the sale to accept payment or put it back on
      ${statusMessage[saleStatus]}.</p>`,
        cancel: 'Continue Sale',
        confirm: 'Discard Changes',
      })
    },

    /**
     * Display the errored sales modal.
     *
     * @method viewErroredSales
     *
     * @return {Modal<Promise>} The Modal instance
     */
    viewErroredSales() {
      const retailerSettings = retailerSettingsManager.get()
      const registerData = {
        clientVersion: WEB_REGISTER.CLIENT_VERSION,
        domainPrefix: retailerSettings.domain_prefix,
        retailerId: retailerSettings.id,
        userId: userManager.getLoggedInUser().id,
      }

      return $q
        .all({
          erroredSales: saleSyncManager.getErroredSales(),
          erroredSalesFileUrl: saleSyncManager.generateErroredSalesFile(
            registerData
          ),
        })
        .then(({ erroredSales, erroredSalesFileUrl }) => {
          return this._erroredSalesModal(erroredSales, erroredSalesFileUrl)
        })
        .then(modal => modal.closed)
        .then(() => saleSyncManager.clearErroredSalesFile())
    },

    /**
     * Construct the errored sales modal.
     *
     * @private
     * @method _erroredSalesModal
     *
     * @param {Array<Sale>} erroredSales
     *        Collection of errored sales
     *
     * @param {String} erroredSalesFileUrl
     *        The download url for the errored sales
     */
    _erroredSalesModal(erroredSales: Sale[], erroredSalesFileUrl: string) {
      return Modal({
        template: erroredSalesTemplate,
        controller: 'ErroredSalesModalController',
        controllerAs: '$ctrl',
        erroredSales,
        erroredSalesFileUrl,
      })
    },

    /**
     * Navigate to the sell screen
     *
     * @private
     *
     * @method _navSellScreen
     *
     * @return {Promise} Resolved with changing state to the sell screen
     */
    _navSellScreen() {
      return $state.go(STATES.SALE)
    },
  }
}

export type SaleModals = ReturnType<typeof SaleModalsFactory>

/**
 * Provides sale related modals e.g. to action an existing sale while loading another.
 *
 * @module saleModals
 */
angular.module('webregister.modals').factory('saleModals', SaleModalsFactory)
